export const CHILD_SECTIONS = ['today', 'tasks', 'activities', 'tools', 'voice', 'school', 'class-schedule', 'sports', 'messages', 'settings', 'profile', 'phone', 'email', 'assistant', 'permissions'] as const;
export type ChildSection = typeof CHILD_SECTIONS[number];

export function hasFamilyPermissions(me: {
  policyVersionRequired?: string[];
  consents?: { type: string; policy_version: string; student_id?: string | null }[];
  policyVersion?: string;
  earlyReleaseDisclaimerVersion?: string;
}) {
  return (me.policyVersionRequired ?? []).every((type) => (me.consents ?? []).some((consent) =>
    consent.student_id == null && consent.type === type &&
    consent.policy_version === (type === 'early_release_disclaimer' ? me.earlyReleaseDisclaimerVersion : me.policyVersion)));
}

export function childLocation(search: string, parent: boolean) {
  const params = new URLSearchParams(search);
  const requested = params.get('tab');
  const section: ChildSection = params.get('calendar') === 'select_required'
    ? 'class-schedule'
    : requested === 'more' ? 'tools'
    : CHILD_SECTIONS.includes(requested as ChildSection) ? requested as ChildSection : 'today';
  return { section, setup: parent && params.has('setup') };
}

export function initialOnboarding(worker: { status?: string; activated_at?: string } | undefined) {
  return worker?.status === 'setup_required' && !worker.activated_at;
}

export function nextSetupGate(state: { permissionsReady: boolean; assignmentSourceReady: boolean; phoneVerified: boolean }, ready: boolean) {
  if (!state.permissionsReady) return 'permissions';
  if (!state.assignmentSourceReady) return 'school';
  if (!state.phoneVerified) return 'phone';
  return ready ? 'activate' : 'permissions';
}

export type AssistantReadiness = {
  assistantEnabled: boolean;
  smsReady: boolean;
  voiceTrusted: boolean;
  phoneRepairNeeded: boolean;
  primaryRepair: 'phone' | 'school' | 'permissions' | null;
};

function contactValue(contact: any, snake: string, camel: string) {
  return contact?.[snake] ?? contact?.[camel];
}

export function assistantReadiness(input: {
  worker?: { status?: string; activated_at?: string | null } | null;
  readiness?: { smsDestinationReady?: boolean; assignmentSource?: { ready?: boolean } } | null;
  contacts?: any[];
  smsContacts?: any[];
  voiceTrustedUntil?: string | null;
  permissionsReady?: boolean;
}): AssistantReadiness {
  const contacts = input.smsContacts ?? input.contacts ?? [];
  const smsReady = input.readiness?.smsDestinationReady === true || contacts.some((contact) =>
    contactValue(contact, 'channel', 'channel') === 'sms' &&
    Boolean(contactValue(contact, 'verified_at', 'verifiedAt')) &&
    Boolean(contactValue(contact, 'consent_at', 'consentAt')) &&
    !contactValue(contact, 'opted_out_at', 'optedOutAt') &&
    contactValue(contact, 'sms_subscription_status', 'smsSubscriptionStatus') !== 'unsubscribed' &&
    contactValue(contact, 'status', 'status') !== 'unsubscribed');
  const assistantEnabled = input.worker?.status === 'active';
  const voiceTrusted = Boolean(input.voiceTrustedUntil && new Date(input.voiceTrustedUntil).getTime() > Date.now());
  const assignmentReady = input.readiness?.assignmentSource?.ready === true;
  const permissionsReady = input.permissionsReady !== false;
  return {
    assistantEnabled,
    smsReady,
    voiceTrusted,
    phoneRepairNeeded: assistantEnabled && !smsReady,
    primaryRepair: !permissionsReady ? 'permissions' : !assignmentReady ? 'school' : !smsReady ? 'phone' : null,
  };
}

export function familyStudentNextStep(blockers: string[]): string | null {
  if (!blockers.length) return null;
  if (blockers.some((b) =>
    /^parent has not accepted the current .+ consent$/i.test(b) ||
    /guardian|family permissions/i.test(b))) return 'Review the required family permissions';
  if (blockers.some((b) => b.toLowerCase().includes('early release'))) {
    return 'Review and accept the current Early Release Notice';
  }
  if (blockers.some((b) => /canvas|assignment source/i.test(b))) {
    return 'Connect the school feed or Canvas and complete the first successful sync';
  }
  if (blockers.some((b) => /phone|sms/i.test(b))) {
    return 'Verify the student phone number and SMS consent';
  }
  return blockers[0];
}

export function familyPrimaryAction(permissionsReady: boolean, childCount: number) {
  if (!permissionsReady) return 'review_permissions';
  return childCount > 0 ? 'add_another_child' : 'add_first_child';
}
